import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Briefcase, MessageSquare, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

function ResumeUploadDialog() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = new FormData(e.currentTarget);
      if (fileRef.current?.files?.[0]) {
        form.set("resume", fileRef.current.files[0]);
      }
      await api.post("/candidates", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile submitted! We'll be in touch soon.");
      setOpen(false);
      e.currentTarget.reset();
      setFileName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="candidate-upload-resume-btn" className="w-full bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full">
          Upload Resume <Upload className="ml-2 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Register as a candidate</DialogTitle>
          <DialogDescription>Drop your details and resume — we'll match you with the right role.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="candidate-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input id="full_name" name="full_name" required data-testid="candidate-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" required data-testid="candidate-email" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" name="phone" required data-testid="candidate-phone" />
            </div>
            <div>
              <Label htmlFor="current_title">Current Title / Fresher</Label>
              <Input id="current_title" name="current_title" data-testid="candidate-title" />
            </div>
          </div>
          <div>
            <Label htmlFor="preferred_locations">Preferred Location(s)</Label>
            <Input id="preferred_locations" name="preferred_locations" placeholder="Bengaluru, Pune, Remote" data-testid="candidate-locations" />
          </div>
          <div>
            <Label htmlFor="skills">Skills / Domain</Label>
            <Input id="skills" name="skills" placeholder="Java, Spring, AWS" data-testid="candidate-skills" />
          </div>
          <div>
            <Label htmlFor="resume">Resume (PDF / DOC / DOCX, max 5MB)</Label>
            <label
              htmlFor="resume"
              className="mt-1 flex items-center justify-center gap-2 w-full border-2 border-dashed border-border rounded-lg px-4 py-6 cursor-pointer hover:border-bee-teal hover:bg-secondary/40 transition-colors"
              data-testid="candidate-resume-drop"
            >
              <Upload className="h-5 w-5 text-bee-teal" />
              <span className="text-sm">{fileName || "Click to upload resume"}</span>
            </label>
            <input
              id="resume"
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
            />
          </div>
          <Button type="submit" disabled={submitting} data-testid="candidate-submit-btn" className="w-full bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full h-11">
            {submitting ? "Submitting..." : "Submit Profile"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ActionCard = ({ icon: Icon, title, desc, action, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-border bg-card p-6 card-lift flex flex-col">
    <div className="h-12 w-12 rounded-xl bg-bee-orange/10 text-bee-orange inline-flex items-center justify-center">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>
    <div className="mt-5">{action}</div>
  </div>
);

export default function Candidates() {
  const navigate = useNavigate();
  return (
    <section id="candidates" className="py-20 lg:py-28" data-testid="candidates-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-bee-orange font-semibold mb-3">For Candidates</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Looking for your{" "}
            <span className="text-bee-teal">next opportunity?</span>
          </h2>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground">
            We don&apos;t just fill positions — we build careers. Register with us and get matched to roles
            that fit your skills, experience, and goals.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <ActionCard
            testId="candidate-card-upload"
            icon={FileText}
            title="Upload your resume"
            desc="Submit your CV and our team will reach out when a matching role opens up."
            action={<ResumeUploadDialog />}
          />
          <ActionCard
            testId="candidate-card-jobs"
            icon={Briefcase}
            title="Browse open jobs"
            desc="View all active job listings and apply directly online."
            action={
              <Button
                onClick={() => navigate("/jobs")}
                data-testid="candidate-view-jobs-btn"
                variant="outline"
                className="w-full border-bee-teal/30 text-bee-teal hover:bg-bee-teal hover:text-white rounded-full"
              >
                View Jobs
              </Button>
            }
          />
          <ActionCard
            testId="candidate-card-guidance"
            icon={MessageSquare}
            title="Get career guidance"
            desc="Speak to one of our recruitment consultants for personalized career advice."
            action={
              <Button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="candidate-book-call-btn"
                variant="outline"
                className="w-full border-bee-orange/30 text-bee-orange hover:bg-bee-orange hover:text-white rounded-full"
              >
                Book a Call
              </Button>
            }
          />
        </div>
      </div>
    </section>
  );
}
